import {
  type DatabaseField,
  type DatabaseFieldType,
  DisplayedDetailType
} from '@databases/types/database';
import { createElement, type ReactNode } from 'react';
import {
  type ContentColumnReadPolicy,
  type ContentColumnValue,
  type ContentColumnReadContent
} from './content-column-read.ts';
import { ContentColumnValueView } from './content-column-value-view.tsx';
import {
  defineFieldReadModule,
  type FieldReadResource,
  type FieldReadResourceState
} from './field-read-module.ts';
import { getReferencedPageLinks } from './referenced-page-links.ts';

/**
 * The content-column field family shares V1's addressed read and presentation rules.
 */
export function createContentColumnFieldReadModule<Kind extends string>(
  definition: {
    readonly fieldType: DatabaseFieldType;
    readonly emptyContent: ContentColumnReadContent<Kind>;
    readonly getColumn: (field: DatabaseField) => string | undefined;
  },
  options?: ContentColumnReadPolicy & {
    readonly render?: (content: ContentColumnReadContent<Kind>) => ReactNode;
  }
) {
  return defineFieldReadModule<ContentColumnReadContent<Kind>>({
    fieldType: definition.fieldType,
    render:
      options?.render ??
      ((content) =>
        createElement(ContentColumnValueView, {
          content,
          renderValue: options?.renderDatasourceValue ?? (() => null)
        })),
    compile(field: DatabaseField) {
      const referencedFieldId = field.settings.fieldId;
      const configuredColumn = definition.getColumn(field);
      const column =
        configuredColumn === 'versionNumber' && field.settings.includeVersionComment
          ? 'versionComment'
          : (configuredColumn ?? '');
      const loadingWidth = options?.getLoadingWidth?.(field) ?? 0;
      const openLinksInNewTab =
        field.settings.displayedDetail === DisplayedDetailType.EXCERPT;

      return Object.freeze({
        fieldId: field.id,
        resolve: () => definition.emptyContent,
        getResourceRead({ database, address }) {
          if (!options || !referencedFieldId) {
            return undefined;
          }

          const references = getReferencedPageLinks(database, address, referencedFieldId);

          if (!references.length) {
            return undefined;
          }

          const resources = new Map<string, FieldReadResource<ContentColumnValue>>();
          const entries = references.map((reference) => {
            const input = {
              contentId: reference.contentId,
              ari: reference.ari,
              connectionId: referencedFieldId,
              column
            };
            const cached = options.getContentColumnSnapshot?.(input);

            if (cached && !(cached.kind === 'empty' && cached.pending)) {
              return { reference, cached, resource: undefined };
            }

            const resource = options.getContentColumnResource(input);

            resources.set(resource.key, resource);

            return { reference, cached, resource };
          });
          const resolve = (
            read: <Value>(
              resource: FieldReadResource<Value>
            ) => FieldReadResourceState<Value>
          ): ContentColumnReadContent<Kind> => {
            const states = entries.map(
              ({ cached, resource }): FieldReadResourceState<ContentColumnValue> => {
                const state = resource
                  ? read(resource)
                  : { status: 'unavailable' as const };

                return cached && state.status !== 'ready'
                  ? { status: 'ready', value: cached }
                  : state;
              }
            );
            /**
             * V1 compacts loaded content before pairing it with the reference status rows.
             * Preserve that ordering; changing it requires a shared V1/V2 behavior change.
             */
            const loaded = states.filter(
              (state) => state.status === 'ready' && state.value.kind !== 'unavailable'
            );

            return Object.freeze({
              kind: definition.emptyContent.kind,
              loadingWidth,
              openLinksInNewTab,
              items: Object.freeze(
                entries.map(({ reference }, index) =>
                  Object.freeze({
                    key: reference.key,
                    state:
                      loaded[index] ??
                      (states[index].status === 'ready'
                        ? { status: 'unavailable' as const }
                        : states[index])
                  })
                )
              )
            });
          };
          const loadingContent = resolve(() => ({ status: 'loading' }));

          return Object.freeze({
            resources: Object.freeze([...resources.values()]),
            resolve(
              read: <Value>(
                resource: FieldReadResource<Value>
              ) => FieldReadResourceState<Value>
            ) {
              let completed = false;
              const content = resolve((resource) => {
                const state = read(resource);

                completed ||= state.status !== 'loading';

                return state;
              });

              return completed ? content : loadingContent;
            }
          });
        }
      });
    }
  });
}
